import { Suspense, forwardRef, useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import type { LayoutChangeEvent, TextInput } from 'react-native';
import { View } from 'react-native';

import { Form, theme } from '@squire/ui';

import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { BottomSheet } from '@/components/_shared/BottomSheet';
import { CancellationPolicy } from '@/components/_shared/CancellationPolicy';
import { PaymentMethodSelector } from '@/components/_shared/PaymentMethodSelector';
import { PaymentMethodSkeleton } from '@/components/_shared/PaymentMethodSkeleton';
import { ScreenHeader } from '@/components/_shared/ScreenHeader';
import { ShopDetails } from '@/components/_shared/ShopDetails';
import { UserInformationForm } from '@/components/_shared/UserInformationForm';
import { WalletDetector } from '@/components/_shared/WalletDetector';
import { ConfirmBookingSkeleton } from '@/components/booking/ConfirmBookingSkeleton';
import { getCountryCodeForName } from '@/constants/countries';
import { storage, storageKeys } from '@/constants/storage';
import { useLegalBottomSheet } from '@/context/legal';
import type { WalletAvailability } from '@/context/walletAvailability';
import { WalletAvailabilityContext } from '@/context/walletAvailability';
import { useConfirmOrderForm } from '@/hooks/booking/useConfirmOrderForm';
import {
  buildCartRevision,
  useSyncGuestCustomerToCart,
} from '@/hooks/booking/useSyncGuestCustomerToCart';
import { useBnplCheckoutVariant } from '@/hooks/payments/useBnplCheckoutVariant';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { StripeProvider } from '@/providers/StripeProvider';
import composeRefs from '@/utils/composeRefs';
import { Logger } from '@/utils/logging';
import type { PaymentMethod } from '@/utils/paymentMethods';
import { Tracker } from '@/utils/tracking';

import { ConfirmBookingFooter } from '../ConfirmBookingFooter';
import { ConfirmBookingSignInPrompt } from '../ConfirmBookingSignInPrompt';
import { ConfirmBookingSummary } from '../ConfirmBookingSummary';
import { styles } from './styles';
import type { ConfirmBookingBottomSheetProps } from './types';

const isSamePaymentMethod = (
  a: PaymentMethod | undefined,
  b: PaymentMethod | undefined,
): boolean => {
  if (!a || !b) return false;
  return a.type === b.type && a.id === b.id;
};

const ConfirmBookingBottomSheet = forwardRef<
  BottomSheetRef,
  ConfirmBookingBottomSheetProps
>(
  (
    {
      cart,
      shop,
      onDismiss,
      onBook,
      onBookPress,
      onBookSubmitFailed,
      isLoadingCart,
      isCheckingOutCart,
      isBooking,
      onSetPromoCode,
      removeGiftCardCode,
      onValidateGuestMembership,
      testID = 'confirm-booking-bottom-sheet',
      isWaitlistMode = false,
      waitlistRequiresPayment = false,
    },
    ref,
  ) => {
    const { t } = useScreenTranslations('ConfirmBookingBottomSheet');
    const { openTerms, openPrivacy } = useLegalBottomSheet();
    const [walletAvailability, setWalletAvailability] =
      useState<WalletAvailability>({ googlePay: false, applePay: false });
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const hasTrackedDetailsViewedRef = useRef(false);
    const firstFieldRef = useRef<TextInput>(null);
    const previousMethodRef = useRef<PaymentMethod | undefined>(undefined);
    const pendingAnchorRef = useRef(false);
    const measuredYRef = useRef<number | null>(null);
    const bnplVariant = useBnplCheckoutVariant(cart, shop);

    const {
      form,
      selectedPaymentMethod,
      isFormValid,
      invalidUserInformation,
      handleSubmit: handleBook,
      handleDismiss,
      closeBottomSheet,
      bottomSheetRef,
      customer,
      isCustomerSettling,
    } = useConfirmOrderForm({
      onSubmit: (data) => {
        Tracker.track('booking_confirm_button_clicked', {
          paymentMethod: (data.paymentMethod as PaymentMethod)?.type ?? '',
          shopId: shop?.id ?? '',
          cartId: cart?.id ?? '',
        });

        return onBook({
          ...data,
          paymentMethod: data.paymentMethod as PaymentMethod,
        });
      },
      onSubmitError: (error) => {
        onBookSubmitFailed?.();
        Logger.error('Error submitting booking form', { error });
      },
      onDismiss: () => {
        storage.remove(storageKeys.authReturnPath);
        setIsSheetOpen(false);
        hasTrackedDetailsViewedRef.current = false;
        onDismiss?.();
      },
    });

    // SQR-14053 Story 4: the contact form only ever mounts under this
    // condition — a guest who has picked a method, or anyone whose entered
    // details are invalid. Shared between the render below and the focus
    // anchor's mount precondition so they can't drift apart.
    const isContactFormMounted = Boolean(
      (!customer && selectedPaymentMethod) || invalidUserInformation,
    );

    // useWatch, not form.watch: with the React Compiler enabled a `watch(name)`
    // call is memoized on its stable args and never re-reads RHF's mutable
    // state, so edits never re-triggered validation (SQR-15069).
    const watchedFirstName = useWatch({
      control: form.control,
      name: 'firstName',
    });
    const watchedLastName = useWatch({
      control: form.control,
      name: 'lastName',
    });
    const watchedEmail = useWatch({ control: form.control, name: 'email' });
    const watchedPhone = useWatch({ control: form.control, name: 'phone' });

    const { isPendingSync, isValidating, validatedCart } =
      useSyncGuestCustomerToCart({
        enabled: Boolean(
          !customer &&
            !isCustomerSettling &&
            isSheetOpen &&
            !isCheckingOutCart &&
            !isBooking,
        ),
        firstName: watchedFirstName,
        lastName: watchedLastName,
        email: watchedEmail,
        phone: watchedPhone,
        // Re-validate when the cart itself changes — e.g. removing a promo
        // un-blocks the membership discount (SQR-15069).
        cartRevision: buildCartRevision(cart),
        onValidate: onValidateGuestMembership,
      });

    // Identify the guest whenever they apply a code. The backend only evaluates
    // a promo against a customer, so this is what surfaces both "not eligible
    // for this promotion" and the membership/promo conflict at entry instead of
    // letting the code into the breakdown and failing at Book (SQR-15069).
    const handleSetPromoCode = (code: string | null) => {
      const canIdentifyGuest =
        !customer && !!watchedFirstName && !!(watchedEmail || watchedPhone);

      onSetPromoCode?.(
        code,
        canIdentifyGuest
          ? {
              firstName: watchedFirstName,
              lastName: watchedLastName ?? '',
              email: watchedEmail ?? '',
              phone: watchedPhone ?? '',
            }
          : undefined,
      );
    };

    const runAnchor = () => {
      const y = measuredYRef.current;
      if (y === null) return;

      pendingAnchorRef.current = false;

      bottomSheetRef?.current?.scrollTo?.({
        y: Math.max(y - theme.spacing[4], 0),
        animated: true,
      });

      if (!form.getValues('firstName')) {
        firstFieldRef?.current?.focus?.();
      }
    };

    useEffect(() => {
      const previousMethod = previousMethodRef.current;
      previousMethodRef.current = selectedPaymentMethod;

      if (
        !selectedPaymentMethod ||
        isSamePaymentMethod(previousMethod, selectedPaymentMethod) ||
        !isContactFormMounted
      ) {
        return;
      }

      pendingAnchorRef.current = true;

      if (measuredYRef.current !== null) {
        runAnchor();
      }
    }, [selectedPaymentMethod, isContactFormMounted]); // eslint-disable-line react-hooks/exhaustive-deps

    const onAnchorLayout = (event: LayoutChangeEvent) => {
      measuredYRef.current = event?.nativeEvent?.layout?.y ?? null;

      if (pendingAnchorRef.current) {
        runAnchor();
      }
    };

    const tipsTotal =
      cart?.items.reduce((sum, item) => sum + (item.tipAmount || 0), 0) || 0;

    // SQR-14035: the sheet is where clients pick a payment method and enter
    // contact info, so it is tracked as "checkout details viewed" rather than
    // by its "Confirm booking" UI label. We wait for cart.breakdown and for the
    // customer query to settle before firing: on a cold open the sheet shows a
    // skeleton, and firing early would report money fields as 0 and a signed-in
    // client as hasAccount:false. Fires once per open cycle; the dismiss handler
    // above resets the guard so a re-open fires a fresh event.
    const isCheckoutDetailsReady =
      isSheetOpen && !!cart?.breakdown && !isCustomerSettling;

    useEffect(() => {
      if (!isCheckoutDetailsReady || hasTrackedDetailsViewedRef.current) return;

      hasTrackedDetailsViewedRef.current = true;

      Tracker.track('checkout_details_viewed', {
        shopId: shop?.id ?? '',
        cartId: cart?.id ?? '',
        currency: cart?.currency ?? '',
        totalDollars: String((cart?.total ?? 0) / 100),
        discountAmount: String((cart?.breakdown?.discountAmount ?? 0) / 100),
        promoAmount: String((cart?.breakdown?.promoAmount ?? 0) / 100),
        hasGiftCard: String(
          cart?.payments?.some((payment) => payment.type === 'gift_card') ??
            false,
        ),
        tipAmount: String(tipsTotal / 100),
        appointmentCount: String(cart?.items.length ?? 0),
        isWaitlistMode: String(isWaitlistMode),
        hasAccount: String(!!customer),
        servicesByBarber: JSON.stringify(
          cart?.items.map((item) => ({
            serviceIds: item.services.map((service) => service.id),
            barberId: item.barberId,
          })) ?? [],
        ),
      });
    }, [
      isCheckoutDetailsReady,
      cart,
      shop,
      tipsTotal,
      isWaitlistMode,
      customer,
    ]);

    return (
      <BottomSheet
        accessibilityLabel={t('title')}
        hideHandle
        testID={testID}
        dismissible={!isCheckingOutCart}
        onOpen={() => setIsSheetOpen(true)}
        onDismiss={handleDismiss}
        ref={composeRefs(ref, bottomSheetRef)}
        style={styles.bottomSheet}
        containerStyle={styles.background}
        // Size to content, but allow up to 95% of the screen (overrides the
        // shared BottomSheet's 90% default) before the inner content scrolls.
        maxDynamicContentRatio={0.95}
        scrollProps={{
          showsVerticalScrollIndicator: false,
        }}
        // Pinned below the scrolling content so Book stays reachable however
        // long the payment method list grows (SQR-17561).
        footer={
          isCustomerSettling ? undefined : (
            <ConfirmBookingFooter
              onBook={() => {
                onBookPress?.(selectedPaymentMethod as PaymentMethod);
                handleBook();
              }}
              // A pending validation blocks booking, but must not claim one is
              // happening — the spinner is reserved for real async work.
              disabled={!isFormValid || isPendingSync}
              onTermsPress={openTerms}
              onPrivacyPress={openPrivacy}
              loading={isLoadingCart || isCheckingOutCart || isBooking}
              isWaitlistMode={isWaitlistMode}
            />
          )
        }
        footerStyle={styles.footer}
      >
        <View>
          <ScreenHeader title={t('title')} onClose={closeBottomSheet} />

          {!isCustomerSettling && !customer && <ConfirmBookingSignInPrompt />}
        </View>

        {isCustomerSettling ? (
          <ConfirmBookingSkeleton />
        ) : (
          <Form {...form}>
            <ShopDetails shop={shop} />

            <Suspense fallback={<PaymentMethodSkeleton />}>
              <StripeProvider>
                <WalletDetector
                  onReady={(methods) =>
                    setWalletAvailability((prev) => ({ ...prev, ...methods }))
                  }
                />
                <WalletAvailabilityContext.Provider value={walletAvailability}>
                  {/* SQR-12978: payment-method visibility (e.g. Pay in Person) is
                      derived from cart.breakdown, so require the breakdown to be
                      present before rendering the selector — we never decide
                      against a half-loaded cart. We deliberately do NOT gate on
                      isLoadingCart: once a breakdown exists, a background refetch
                      (e.g. after tapping Book) should not flip the selector back
                      into a skeleton. */}
                  {cart && shop && cart.breakdown ? (
                    <PaymentMethodSelector
                      cart={cart}
                      shop={shop}
                      isWaitlistMode={isWaitlistMode}
                      waitlistRequiresPayment={waitlistRequiresPayment}
                      bnplVariant={bnplVariant}
                      isSheetOpen={isSheetOpen}
                      preselectDefaultMethod
                    />
                  ) : (
                    <PaymentMethodSkeleton />
                  )}
                </WalletAvailabilityContext.Provider>
              </StripeProvider>
            </Suspense>

            {isContactFormMounted && (
              <View
                testID="confirm-booking-contact-form-anchor"
                onLayout={onAnchorLayout}
              >
                <UserInformationForm
                  firstFieldRef={firstFieldRef}
                  defaultCountry={getCountryCodeForName(shop?.address?.country)}
                />
              </View>
            )}

            <CancellationPolicy
              bookingInfo={cart?.bookingInfo}
              timezone={shop?.timezone || 'America/New_York'}
            />

            <ConfirmBookingSummary
              breakdown={cart?.breakdown || null}
              tipsTotal={tipsTotal}
              currency={cart?.currency}
              shop={shop || undefined}
              cart={cart || undefined}
              isLoadingCart={isLoadingCart || isCheckingOutCart}
              isUpdatingBreakdown={
                isValidating || isLoadingCart || isCheckingOutCart
              }
              validatedCart={validatedCart}
              onSetPromoCode={handleSetPromoCode}
              selectedPaymentMethod={selectedPaymentMethod}
              removeGiftCardCode={removeGiftCardCode}
              isWaitlistMode={isWaitlistMode}
            />
          </Form>
        )}
      </BottomSheet>
    );
  },
);

ConfirmBookingBottomSheet.displayName = 'ConfirmBookingBottomSheet';
export { ConfirmBookingBottomSheet };
